import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../../components/PageHeader";
import { prettyStatus } from "../../components/PaperForm/styles";
import NcrPiecesPanel from "../../components/QDP/NcrPiecesPanel"; // CR-2026-QDP
import ApiCall from "../../utils/apicall";
import { todayLocalISO } from "../../utils/dateFmt";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import {
  Container,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TableContainer,
  Paper,
  Stack,
  Typography,
  Breadcrumbs,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  Box,
  IconButton,
  Tooltip,
  Divider,
  Collapse,
  Grid,
} from "@mui/material";
import { HomeRounded, AssignmentLate, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import Iconify from "../../components/iconify";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import KPI_CHECKLISTS from "../../config/kpiConfig";
// Phase 1.1 NCR UI integration (2026-05-05): GR §5.7 finding class + GR §6.1 deadline countdown
import { FINDING_CLASS_LABELS, FINDING_CLASS_COLORS } from "../../utils/ncrFindingClassApi";
import CarDeadlineCountdown from "../../components/CarDeadlineCountdown/CarDeadlineCountdown";
// E-sign attribution (F-F3 closure 2026-07-19): who dispositioned / closed this
// NCR and when — ISO 9001 §10.2 + AISC GR §6.2 traceability.
import ApprovalSignOff from "../../components/ApprovalSignOff";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── constants ───────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Open", value: "open" },
  { label: "Under Review", value: "under-review" },
  { label: "Closed", value: "closed" },
  { label: "Voided", value: "voided" },
];

const STATUS_COLORS = {
  open: "error",
  "under-review": "warning",
  closed: "success",
  voided: "default",
};

const DISPOSITION_LABELS = {
  pending: "Pending",
  "use-as-is": "Use As-Is",
  rework: "Rework",
  repair: "Repair",
  scrap: "Scrap",
  "return-to-vendor": "Return to Vendor",
};

// Built fresh each time so the default date is today (local), even if the
// page has been open since yesterday.
const emptyForm = () => ({
  raised_date: todayLocalISO(),
  job_id: "",
  purchase_order_no: "",
  part_description: "",
  part_number: "",
  heat_number: "",
  quantity_affected: "",
  description: "",
  disposition: "pending",
  root_cause: "",
  corrective_action: "",
  assigned_to: "",
  ncr_level: "",
  threshold_basis: "",
  threshold_hours: "",
  threshold_cost: "",
  final_inspection_note: "",
  rcca_stage: "",
  lessons_learned_ref: "",
  lessons_learned_tsi_tag: "",
});

// TSI Gate (patent-pending Tools/Skills/Information root-cause triad) — PR-1.15.0 section 02A
// CR-2026-CA-TSI-F1151: The TSI Process. One NCR, one gap type, set on the TSI worksheet (F1.15.1).
// No Gap and Pending are decision outcomes, not a fourth leg.
const TSI_GAP_LABEL = { T: "Tools gap", S: "Skills gap", I: "Information gap", NO_GAP: "No gap (escalated)", PENDING: "Pending" };
const TSI_READY = ["T", "S", "I", "NO_GAP"];

const RCCA_STAGES = [
  { key: "CONTAIN", label: "Contain", owner: "Inspector / Site Lead", actions: "Stop work / quarantine \u00b7 tag nonconforming items \u00b7 notify client / 3rd party (per contract) \u00b7 preserve evidence (photos, measurements, heat #s)." },
  { key: "IDENTIFY", label: "Identify", owner: "Inspector + QA Lead", actions: "Run the TSI Gate \u00b7 classify the root gap I \u00b7 S \u00b7 T (may be compound)." },
  { key: "VERIFY", label: "Verify", owner: "QA Lead", actions: "Prove the identified gap \u2014 pull records (WPS, certs, cal stickers, MTRs). Fishbone only to rule out other factors." },
  { key: "CORRECT", label: "Correct", owner: "Responsible Party", actions: "Targeted to gap type \u2014 I: issue/update document \u00b7 S: training / re-qualification \u00b7 T: repair / replace / recalibrate. Re-inspect to confirm." },
  { key: "PREVENT", label: "Prevent", owner: "QA Lead + Management", actions: "Update procedure / checklist \u00b7 add pre-job TSI verification \u00b7 schedule periodic re-verification \u00b7 communicate change." },
  { key: "CLOSE", label: "Close", owner: "Principal / 3rd-party Rep", actions: "Verify corrective actions effective \u00b7 obtain 3rd-party / client sign-off (when contract requires) \u00b7 enter Lessons Learned with TSI tag \u00b7 file RCCA record." },
];

const TABLE_HEAD = [
  { id: "ncr_number", label: "NCR #" },
  { id: "raised_date", label: "Date" },
  { id: "description", label: "Description" },
  { id: "part_description", label: "Part" },
  { id: "finding_class", label: "Class" },     // Phase 1.1: GR §5.7 5-tier taxonomy
  { id: "disposition", label: "Disposition" },
  { id: "status", label: "Status" },
  { id: "deadline_at", label: "CAR Deadline" }, // Phase 1.1: GR §6.1 30-day countdown
  { id: "raisedBy", label: "Raised By" },
  { id: "assigned", label: "Assigned To" },
];

// ─── component ───────────────────────────────────────────────────────────────

export default function QaNCR() {
  const navigate = useNavigate();
  const { loginUser } = useSelector((s) => s.userSlice);
  const { permissions: userPermissions } = loginUser?.decodedToken || { permissions: [] };
  const canView   = hasPermission(userPermissions, PERMISSIONS.VIEW_NCR);
  const canAdd    = hasPermission(userPermissions, PERMISSIONS.ADD_NCR);
  const canEdit   = hasPermission(userPermissions, PERMISSIONS.EDIT_NCR);
  // DELETE_NCR no longer shown: NCRs are voided with a reason, never deleted (CR-2026-BE-NCR-DECISION-INTEGRITY).
  const canClose  = hasPermission(userPermissions, PERMISSIONS.CLOSE_NCR);

  const [ncrs, setNcrs] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusTab, setStatusTab] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // dialogs
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [openStatusDlg, setOpenStatusDlg] = useState(false);

  const [formData, setFormData] = useState(emptyForm);
  const [selectedNcr, setSelectedNcr] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [formErrors, setFormErrors] = useState({});

  // QCI reference panel
  const [kpiPanelOpen, setKpiPanelOpen] = useState(false);

  // ── fetch ──────────────────────────────────────────────────────────────────

  const fetchNcrs = useCallback(async () => {
    try {
      const params = {};
      if (statusTab) params.status = statusTab;
      if (search.trim()) params.search = search.trim();
      const res = await ApiCall.get("/ncr", { params });
      setNcrs(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("fetchNcrs error:", err);
      setNcrs([]);
    }
  }, [statusTab, search]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await ApiCall.get("/user");
      const raw = res.data?.data?.users;
      setUsers(Array.isArray(raw) ? raw : []);
    } catch (err) {
      // non-critical — Assigned To dropdown will be empty if this fails
    }
  }, []);

  useEffect(() => {
    fetchNcrs();
  }, [fetchNcrs]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ── helpers ────────────────────────────────────────────────────────────────

  const validate = (data) => {
    const errs = {};
    if (!data.description || !data.description.trim()) {
      errs.description = "Description is required";
    }
    return errs;
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setFormErrors({});
  };

  const userName = (user) =>
    user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "—";

  // ── add ────────────────────────────────────────────────────────────────────

  const handleOpenAdd = () => {
    resetForm();
    setOpenAdd(true);
  };

  const handleAddSubmit = async () => {
    const errs = validate(formData);
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    try {
      await ApiCall.post("/ncr", {
        ...formData,
        job_id: formData.job_id || null,
        assigned_to: formData.assigned_to || null,
        quantity_affected: formData.quantity_affected || null,
      });
      toast("NCR created successfully");
      setOpenAdd(false);
      resetForm();
      fetchNcrs();
    } catch (err) {
      console.error("createNcr error:", err);
      toast.error("Failed to create NCR");
    }
  };

  // ── edit ───────────────────────────────────────────────────────────────────

  const handleOpenEdit = (ncr) => {
    setSelectedNcr(ncr);
    setFormData({
      raised_date: ncr.raised_date || "",
      job_id: ncr.job_id || "",
      purchase_order_no: ncr.purchase_order_no || "",
      part_description: ncr.part_description || "",
      part_number: ncr.part_number || "",
      heat_number: ncr.heat_number || "",
      quantity_affected: ncr.quantity_affected || "",
      description: ncr.description || "",
      disposition: ncr.disposition || "pending",
      root_cause: ncr.root_cause || "",
      corrective_action: ncr.corrective_action || "",
      assigned_to: ncr.assigned_to || "",
      ncr_level: ncr.ncr_level || "",
      threshold_basis: ncr.threshold_basis || "",
      threshold_hours: ncr.threshold_hours || "",
      threshold_cost: ncr.threshold_cost || "",
      final_inspection_note: ncr.final_inspection_note || "",
      rcca_stage: ncr.rcca_stage || "",
      lessons_learned_ref: ncr.lessons_learned_ref || "",
      lessons_learned_tsi_tag: ncr.lessons_learned_tsi_tag || "",
    });
    setFormErrors({});
    setOpenEdit(true);
  };

  const handleEditSubmit = async () => {
    const errs = validate(formData);
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    try {
      await ApiCall.put(`/ncr/${selectedNcr.id}`, {
        ...formData,
        job_id: formData.job_id || null,
        assigned_to: formData.assigned_to || null,
        quantity_affected: formData.quantity_affected || null,
      });
      toast("NCR updated successfully");
      setOpenEdit(false);
      fetchNcrs();
    } catch (err) {
      console.error("updateNcr error:", err);
      toast.error("Failed to update NCR");
    }
  };

  // ── status change ──────────────────────────────────────────────────────────

  const handleOpenStatus = (ncr) => {
    setSelectedNcr(ncr);
    setNewStatus(ncr.status);
    setStatusReason("");
    setOpenStatusDlg(true);
  };

  // CR-2026-BE-NCR-DECISION-INTEGRITY (N-5): voiding, or moving an NCR out of
  // closed or voided, needs a written reason. Same rule as the server.
  const statusNeedsReason = (from, to) =>
    (to === "voided" && from !== "voided") ||
    (["closed", "voided"].includes(from) && to !== from);

  const handleStatusSubmit = async () => {
    // review-fix (F-02 / CA-8): a NCR must not close without a documented root cause AND
    // corrective action (AISC GR §5.7/§6.2, ISO 9001 §10.2). Client gate mirrors the
    // server gate so the user gets an immediate, specific message instead of a 422.
    if (newStatus === "closed") {
      const rc = (selectedNcr?.root_cause || "").trim();
      const ca = (selectedNcr?.corrective_action || "").trim();
      if (!rc || !ca) {
        toast.error("Cannot close: a root cause and corrective action are required (edit the NCR first).");
        return;
      }
    }
    const reason = statusReason.trim();
    if (statusNeedsReason(selectedNcr?.status, newStatus) && reason.length < 4) {
      toast.error(newStatus === "voided"
        ? "A reason is required to void an NCR (at least 4 characters)."
        : "A reason is required to reopen a closed or voided NCR (at least 4 characters).");
      return;
    }
    try {
      await ApiCall.put(`/ncr/${selectedNcr.id}/status`, reason ? { status: newStatus, reason } : { status: newStatus });
      toast("NCR status updated");
      setOpenStatusDlg(false);
      fetchNcrs();
    } catch (err) {
      console.error("updateNcrStatus error:", err);
      toast.error(err?.response?.data?.message || "Failed to update status");
    }
  };

  // ── detail view ────────────────────────────────────────────────────────────

  const handleOpenDetail = (ncr) => {
    setSelectedNcr(ncr);
    setOpenDetail(true);
  };

  // ── shared form fields ─────────────────────────────────────────────────────

  const tsiReady = !!(openEdit && selectedNcr && TSI_READY.includes(selectedNcr.tsi_gap_type));

  const renderFormFields = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
      <TextField
        label="Date Raised"
        type="date"
        fullWidth
        InputLabelProps={{ shrink: true }}
        value={formData.raised_date}
        onChange={(e) => setFormData({ ...formData, raised_date: e.target.value })}
      />
      <TextField
        label="Description of Non-Conformance *"
        fullWidth
        multiline
        rows={3}
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        error={!!formErrors.description}
        helperText={formErrors.description}
      />
      <TextField
        label="Part Description"
        fullWidth
        value={formData.part_description}
        onChange={(e) => setFormData({ ...formData, part_description: e.target.value })}
      />
      <Stack direction="row" spacing={1}>
        <TextField
          label="Part Number"
          fullWidth
          value={formData.part_number}
          onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
        />
        <TextField
          label="Heat Number"
          fullWidth
          value={formData.heat_number}
          onChange={(e) => setFormData({ ...formData, heat_number: e.target.value })}
        />
        <TextField
          label="Qty Affected"
          type="number"
          fullWidth
          value={formData.quantity_affected}
          onChange={(e) => setFormData({ ...formData, quantity_affected: e.target.value })}
        />
      </Stack>
      <TextField
        label="Purchase Order No"
        fullWidth
        value={formData.purchase_order_no}
        onChange={(e) => setFormData({ ...formData, purchase_order_no: e.target.value })}
      />
      <Box sx={{ border: "1px solid #DDE3EA", borderRadius: 2, overflow: "hidden", mt: 1 }}>
        <Box sx={{ bgcolor: "#1D2330", px: 2, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>NCR level and threshold</Typography>
            <Typography sx={{ color: "#C7CCD6", fontSize: 11 }}>Root cause is found with The TSI Process on the TSI worksheet (F1.15.1), before any disposition</Typography>
          </Box>
          <Box sx={{ bgcolor: "#F88F1D", color: "#1D2330", fontWeight: 700, fontSize: 10, px: 1.2, py: 0.4, borderRadius: "10px", whiteSpace: "nowrap" }}>CoreAssure&trade;</Box>
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>NCR Level</InputLabel>
              <Select value={formData.ncr_level} label="NCR Level" onChange={(e) => setFormData({ ...formData, ncr_level: e.target.value })}>
                <MenuItem value="">&mdash; Select &mdash;</MenuItem>
                <MenuItem value="LEVEL_1">Level 1 &mdash; Customer Input</MenuItem>
                <MenuItem value="LEVEL_2">Level 2 &mdash; Drawing Change</MenuItem>
                <MenuItem value="LEVEL_3">Level 3 &mdash; Time &amp; Materials</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Threshold</InputLabel>
              <Select value={formData.threshold_basis} label="Threshold" onChange={(e) => setFormData({ ...formData, threshold_basis: e.target.value })}>
                <MenuItem value="">&mdash; Select &mdash;</MenuItem>
                <MenuItem value="TIME">&gt; 1.5 hr (time)</MenuItem>
                <MenuItem value="COST">&gt; $500 (cost)</MenuItem>
                <MenuItem value="BOTH">Both</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Hours" type="number" value={formData.threshold_hours} onChange={(e) => setFormData({ ...formData, threshold_hours: e.target.value })} sx={{ width: 120 }} />
            <TextField size="small" label="Cost $" type="number" value={formData.threshold_cost} onChange={(e) => setFormData({ ...formData, threshold_cost: e.target.value })} sx={{ width: 120 }} />
          </Stack>
          {openEdit && selectedNcr && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography variant="body2">
                TSI worksheet gap type: <strong>{selectedNcr.tsi_gap_type ? TSI_GAP_LABEL[selectedNcr.tsi_gap_type] : "Not started"}</strong>
              </Typography>
              <Button size="small" variant="outlined" onClick={() => navigate(`/qa-ncr/${selectedNcr.id}/tsi`)}>Open TSI worksheet</Button>
            </Stack>
          )}
          {openEdit && selectedNcr && <NcrPiecesPanel ncrId={selectedNcr.id} jobId={selectedNcr.job_id} register="ncr-reports" />}
        </Box>
      </Box>
      <FormControl fullWidth>
        <InputLabel>Disposition</InputLabel>
        <Select
          value={formData.disposition}
          label="Disposition"
          onChange={(e) => setFormData({ ...formData, disposition: e.target.value })}
        >
          {Object.entries(DISPOSITION_LABELS).map(([val, label]) => (
            <MenuItem key={val} value={val} disabled={val !== "pending" && val !== formData.disposition && !tsiReady}>{label}</MenuItem>
          ))}
        </Select>
        {!tsiReady && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            No disposition until the TSI worksheet has a gap type (T, S, I or No Gap). PENDING is not a resting place.
          </Typography>
        )}
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Assigned To</InputLabel>
        <Select
          value={formData.assigned_to}
          label="Assigned To"
          onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
        >
          <MenuItem value="">— Unassigned —</MenuItem>
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.firstName} {u.lastName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Root Cause"
        fullWidth
        multiline
        rows={2}
        disabled={tsiReady}
        helperText={tsiReady ? "From the TSI worksheet (the artifact, not the behavior)" : undefined}
        value={formData.root_cause}
        onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })}
      />
      <TextField
        label="Corrective Action"
        fullWidth
        multiline
        rows={2}
        value={formData.corrective_action}
        onChange={(e) => setFormData({ ...formData, corrective_action: e.target.value })}
      />
      <Box sx={{ border: "1px solid #DDE3EA", borderRadius: 2, overflow: "hidden", mt: 1 }}>
        <Box sx={{ bgcolor: "#1D2330", px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>CoreAssure&trade; RCCA Lifecycle</Typography>
          <Typography sx={{ color: "#C7CCD6", fontSize: 11 }}>Contain &rarr; Identify &rarr; Verify &rarr; Correct &rarr; Prevent &rarr; Close</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
            {RCCA_STAGES.map((st, idx) => {
              const active = formData.rcca_stage === st.key;
              return (
                <Box key={st.key} onClick={() => setFormData({ ...formData, rcca_stage: st.key })}
                  sx={{ cursor: "pointer", px: 1.25, py: 0.75, borderRadius: 1.5, border: "1px solid",
                    borderColor: active ? "#F88F1D" : "#DDE3EA", bgcolor: active ? "#FFF4E6" : "#fff",
                    display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: active ? "#F88F1D" : "#C9D0D8", color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{idx + 1}</Box>
                  <Typography sx={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? "#1D2330" : "#5B6470" }}>{st.label}</Typography>
                </Box>
              );
            })}
          </Stack>
          {(() => { const st = RCCA_STAGES.find((x) => x.key === formData.rcca_stage); return st ? (
            <Box sx={{ mt: 1.5, bgcolor: "#F6F8FA", borderRadius: 1.5, p: 1.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#1D2330" }}>{st.label} &middot; owner: {st.owner}</Typography>
              <Typography sx={{ fontSize: 11, color: "#5B6470", mt: 0.5 }}>{st.actions}</Typography>
            </Box>
          ) : null; })()}
          {formData.rcca_stage === "CLOSE" && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1.5 }}>
              <TextField size="small" fullWidth label="Lessons Learned ref" value={formData.lessons_learned_ref} onChange={(e) => setFormData({ ...formData, lessons_learned_ref: e.target.value })} />
              <TextField size="small" label="TSI tag" value={formData.lessons_learned_tsi_tag} onChange={(e) => setFormData({ ...formData, lessons_learned_tsi_tag: e.target.value })} sx={{ width: 150 }} placeholder="e.g. I-gap" />
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );

  // ── render ─────────────────────────────────────────────────────────────────

  if (!canView) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Paper sx={{ p: 5, textAlign: "center" }}>
          <AssignmentLate sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            You don&apos;t have permission to view NCR reports.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>QA / NCR | SFM</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* header */}
        <PageHeader
          breadcrumbs={[{ label: "QA / NCR" }]}
          title="Non-Conformance Reports"
          action={canAdd && (
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={handleOpenAdd}
            >
              New NCR
            </Button>
          )}
          guide={{
              why: "Track and close out work that doesn't meet the drawing or spec — so defects are dispositioned and kept from recurring.",
              who: "QC inspectors raising non-conformances; the QM dispositioning and closing them.",
              what: "Your log of non-conformances, each with a disposition and a TSI root-cause / corrective-action record.",
              how: "Raise an NCR against the affected piece, set the disposition, run the TSI Gate, and work it to Closed.",
              when: "Any time an inspection, weld, dimension, or material doesn't conform — before the piece moves forward.",
              standard: "AISC GR §5.7 · ISO 9001:2015 §10.2",
            }}
        />

        {/* search */}
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <TextField
            size="small"
            placeholder="Search NCR #, description, part..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchNcrs()}
            sx={{ width: 320 }}
            InputProps={{
              endAdornment: (
                <IconButton size="small" onClick={fetchNcrs}>
                  <Iconify icon="eva:search-fill" />
                </IconButton>
              ),
            }}
          />
        </Stack>

        {/* status tabs */}
        <Tabs
          value={statusTab}
          onChange={(_, v) => { setStatusTab(v); setPage(0); }}
          sx={{ mb: 2 }}
        >
          {STATUS_TABS.map((t) => (
            <Tab key={t.value} label={t.label} value={t.value} />
          ))}
        </Tabs>

        {/* TSI Gap Distribution — COPQ by root-cause type (Phase 2) */}
        {(() => {
          const counts = { T: 0, S: 0, I: 0, NO_GAP: 0, PENDING: 0 };
          let gated = 0;
          ncrs.forEach((n) => { if (n.tsi_gap_type) { counts[n.tsi_gap_type] = (counts[n.tsi_gap_type] || 0) + 1; gated++; } });
          const cells = [
            ["T", "Tools", counts.T, "#B5701A"],
            ["S", "Skills", counts.S, "#2E9E5B"],
            ["I", "Information", counts.I, "#1E8AA8"],
            ["\u2014", "No gap", counts.NO_GAP, "#5B6470"],
            ["P", "Pending", counts.PENDING, "#B7791F"],
          ];
          return (
            <Box sx={{ border: "1px solid #DDE3EA", borderRadius: 2, overflow: "hidden", mb: 2 }}>
              <Box sx={{ bgcolor: "#1D2330", px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                  The TSI Process &mdash; gap type by NCR
                </Typography>
                <Typography sx={{ color: "#C7CCD6", fontSize: 11 }}>
                  {gated} of {ncrs.length} NCRs with a TSI worksheet &middot; CoreAssure&trade; &middot; bi-weekly T&middot;S&middot;I trend
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} sx={{ p: 1.5 }}>
                {cells.map(([k, label, n, color]) => (
                  <Box key={label} sx={{ flex: 1, textAlign: "center", py: 0.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 26, color: color, lineHeight: 1 }}>{n}</Typography>
                    <Typography sx={{ fontSize: 11, color: "#5B6470", mt: 0.5 }}>
                      <Box component="span" sx={{ fontWeight: 700, color: color }}>{k}</Box> &middot; {label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          );
        })()}

        {/* table */}
        <Paper>
          <TableContainer>
            <SmartTable tableId="ncrs" lockedColumns={["NCR #", "Disposition", "Status"]}>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((col) => (
                    <TableCell key={col.id}>{col.label}</TableCell>
                  ))}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ncrs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((ncr) => (
                    <TableRow key={ncr.id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", cursor: "pointer", color: "primary.main" }}
                          onClick={() => handleOpenDetail(ncr)}
                        >
                          {ncr.ncr_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{ncr.raised_date}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="body2" noWrap title={ncr.description}>
                          {ncr.description}
                        </Typography>
                      </TableCell>
                      <TableCell>{ncr.part_description || "—"}</TableCell>
                      {/* Phase 1.1: GR §5.7 finding class */}
                      <TableCell>
                        {ncr.finding_class ? (
                          <Chip
                            size="small"
                            label={FINDING_CLASS_LABELS[ncr.finding_class] || ncr.finding_class}
                            color={FINDING_CLASS_COLORS[ncr.finding_class] || "default"}
                            variant="outlined"
                          />
                        ) : "—"}
                        {ncr.repetitive_of_id && (
                          <Tooltip title={`Repetitive of CAR #${ncr.repetitive_of_id} (GR §5.8)`}>
                            <Chip size="small" label="REP" color="error" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={DISPOSITION_LABELS[ncr.disposition] || ncr.disposition}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={prettyStatus(ncr.status)}
                          color={STATUS_COLORS[ncr.status] || "default"}
                        />
                      </TableCell>
                      {/* Phase 1.1: GR §6.1 CAR 30-day deadline countdown */}
                      <TableCell>
                        {ncr.finding_class === "CAR" ? (
                          <CarDeadlineCountdown deadline_at={ncr.deadline_at} />
                        ) : "—"}
                      </TableCell>
                      <TableCell>{userName(ncr.raisedBy)}</TableCell>
                      <TableCell>{ncr.assignedTo ? userName(ncr.assignedTo) : "—"}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="TSI worksheet (F1.15.1)">
                            <IconButton size="small" onClick={() => navigate(`/qa-ncr/${ncr.id}/tsi`)}>
                              <Iconify icon="eva:clipboard-outline" />
                            </IconButton>
                          </Tooltip>
                          {canEdit && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenEdit(ncr)}>
                                <Iconify icon="eva:edit-fill" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canClose && (
                            <Tooltip title="Change Status">
                              <IconButton size="small" onClick={() => handleOpenStatus(ncr)}>
                                <Iconify icon="eva:checkmark-circle-2-outline" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                {ncrs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 6 }}>
                      <AssignmentLate sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No NCR reports found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </SmartTable>
          </TableContainer>
          <TablePagination
            component="div"
            count={ncrs.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>

        {/* ── QCI Reference Panel (Generic Template) ───────────────────────── */}
        <Paper sx={{ mt: 3 }}>
          <Box
            onClick={() => setKpiPanelOpen((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              cursor: "pointer",
              borderRadius: "inherit",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Iconify icon="eva:checkmark-square-2-outline" sx={{ color: "primary.main", fontSize: 22 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  SFM — 50 QCI Reference Checklist
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  5 sequences × 10 inspection KPIs — use when categorising NCRs
                </Typography>
              </Box>
              <Chip label="50 KPIs" size="small" color="primary" variant="outlined" />
            </Stack>
            <ExpandMoreIcon
              sx={{
                color: "text.secondary",
                transition: "transform 0.25s",
                transform: kpiPanelOpen ? "rotate(0deg)" : "rotate(-90deg)",
              }}
            />
          </Box>

          <Collapse in={kpiPanelOpen} timeout={250}>
            <Divider />
            <Box sx={{ px: 3, py: 2 }}>
              <Grid container spacing={2}>
                {Object.entries(KPI_CHECKLISTS).map(([station, items]) => (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={station}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, height: "100%", bgcolor: "background.neutral" }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          display: "block",
                          mb: 1.5,
                          color: "primary.main",
                          fontWeight: 700,
                          letterSpacing: 1,
                          fontSize: "0.65rem",
                        }}
                      >
                        {station === "Cut" ? "Detailing / Cut" : station}
                      </Typography>
                      {items.map((kpi, idx) => (
                        <Stack
                          key={idx}
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                          sx={{ mb: 0.75 }}
                        >
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              flexShrink: 0,
                              borderRadius: 0.5,
                              border: "1.5px solid",
                              borderColor: "divider",
                              mt: "2px",
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: "0.78rem", lineHeight: 1.4 }}>
                            {kpi}
                          </Typography>
                        </Stack>
                      ))}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: "block", textAlign: "right", mt: 2 }}
              >
                ; tenants substitute their own checklist content per the SFM Tenant Authorship Rule
              </Typography>
            </Box>
          </Collapse>
        </Paper>
      </Container>

      {/* ── Add NCR dialog ────────────────────────────────────────────────── */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="md" fullWidth>
        <DialogTitle>Raise New NCR</DialogTitle>
        <DialogContent>{renderFormFields()}</DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenAdd(false); resetForm(); }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSubmit}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit NCR dialog ───────────────────────────────────────────────── */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit NCR — {selectedNcr?.ncr_number}</DialogTitle>
        <DialogContent>{renderFormFields()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── Detail dialog ─────────────────────────────────────────────────── */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="sm" fullWidth>
        {selectedNcr && (
          <>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
                  {selectedNcr.ncr_number}
                </Typography>
                <Chip
                  size="small"
                  label={prettyStatus(selectedNcr.status)}
                  color={STATUS_COLORS[selectedNcr.status] || "default"}
                />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <DetailRow label="Date" value={selectedNcr.raised_date} />
                <DetailRow label="Description" value={selectedNcr.description} />
                <DetailRow label="Part Description" value={selectedNcr.part_description} />
                <DetailRow label="Part Number" value={selectedNcr.part_number} />
                <DetailRow label="Heat Number" value={selectedNcr.heat_number} />
                <DetailRow label="Qty Affected" value={selectedNcr.quantity_affected} />
                <DetailRow label="PO Number" value={selectedNcr.purchase_order_no} />
                <DetailRow label="Disposition" value={DISPOSITION_LABELS[selectedNcr.disposition]} />
                <DetailRow label="NCR Level" value={selectedNcr.ncr_level ? selectedNcr.ncr_level.replace("LEVEL_", "Level ") : "—"} />
                <DetailRow label="TSI Gap" value={selectedNcr.tsi_gap_type ? TSI_GAP_LABEL[selectedNcr.tsi_gap_type] : "—"} />
                <DetailRow label="RCCA Stage" value={selectedNcr.rcca_stage ? selectedNcr.rcca_stage.charAt(0) + selectedNcr.rcca_stage.slice(1).toLowerCase() : "—"} />
                <Divider />
                <DetailRow label="Root Cause" value={selectedNcr.root_cause} />
                <DetailRow label="Corrective Action" value={selectedNcr.corrective_action} />
                <Divider />
                <DetailRow label="Raised By" value={userName(selectedNcr.raisedBy)} />
                <DetailRow label="Assigned To" value={selectedNcr.assignedTo ? userName(selectedNcr.assignedTo) : null} />

                {/* Disposition approval sign-off — renders once the
                    disposition_approved_* snapshot columns exist server-side
                    (migration 2026-07-19-ncr-signoff-columns.sql) and a
                    disposition has been approved. Defensive: absent fields
                    simply render nothing. */}
                {selectedNcr.disposition_approved_by_name && (
                  <ApprovalSignOff
                    label="Disposition approved"
                    status="approved"
                    approvedBy={selectedNcr.disposition_approved_by_name}
                    approvedPosition={selectedNcr.disposition_approved_by_position}
                    approvedAt={selectedNcr.disposition_approved_at}
                  />
                )}

                {/* Close sign-off — snapshot columns preferred; falls back to
                    the closedBy user join (works today, pre-migration). */}
                {(selectedNcr.status === "closed"
                  && (selectedNcr.closed_by_name || selectedNcr.closedBy || selectedNcr.closed_at)) && (
                  <ApprovalSignOff
                    label="Closed"
                    status="closed"
                    approvedStatuses={["closed"]}
                    pendingStatuses={[]}
                    approvedBy={selectedNcr.closed_by_name || userName(selectedNcr.closedBy)}
                    approvedPosition={selectedNcr.closed_by_position ?? selectedNcr.closed_by_pos}
                    approvedAt={selectedNcr.closed_at}
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDetail(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Status change dialog ──────────────────────────────────────────── */}
      <Dialog open={openStatusDlg} onClose={() => setOpenStatusDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update NCR Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="under-review">Under Review</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
              <MenuItem value="voided">Voided</MenuItem>
            </Select>
          </FormControl>
          {statusNeedsReason(selectedNcr?.status, newStatus) && (
            <TextField
              sx={{ mt: 2 }}
              fullWidth
              multiline
              minRows={2}
              label={newStatus === "voided" ? "Reason for voiding *" : "Reason for reopening *"}
              helperText="Recorded on the audit trail. NCRs are never deleted; void one with a reason instead."
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatusDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusSubmit}>Update</Button>
        </DialogActions>
      </Dialog>

    </>
  );
}

// ─── helper sub-component ─────────────────────────────────────────────────────

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
